import {
  REQUEST_LOGIN_EMAIL_PASSWORD,
  REQUEST_LOGIN_GOOGLE,
  LOGIN_SUCCESS,
  LOGIN_FAILURE,
  LOGOUT,
  REQUEST_REGISTER_EMAIL_PASSWORD,
  REGISTER_SUCCESS,
  REGISTER_FAILURE,
  USER_AUTHENTICATED,
  USER_UNAUTHENTICATED,
  DELETE_ACCOUNT_SUCCESS,
  REQUEST_DELETE_ACCOUNT,
  DELETE_ACCOUNT_FAILURE,
  PASSWORD_RESET_EMAIL_SENT
} from "../actions/types";

export const ACCOUNT_DELETION_STATUSES = {
  NO_ATTEMPT: "NO_ATTEMPT",
  FAILED: "FAILED",
  SUCCEEDED: "SUCCEEDED"
};

/** How the user tried to log in, so a failure can explain itself accurately. */
export const LOGIN_METHODS = {
  EMAIL: "EMAIL",
  GOOGLE: "GOOGLE"
} as const;

export type LoginMethod = (typeof LOGIN_METHODS)[keyof typeof LOGIN_METHODS];

/** Authentication slice of the store. */
export interface AuthState {
  isLoggedIn: boolean;
  uid: string;
  accountDeletionStatus: string;
  registerFailure: boolean;
  loginFailure: boolean;
  /** Which login attempt failed; null when nothing has failed. */
  loginFailureMethod: LoginMethod | null;
  passwordRecovery: boolean;
}

/** Payload fields the auth reducer reads. */
export interface AuthAction {
  type: string;
  uid?: string;
  method?: LoginMethod;
}

export const initialAuthState: AuthState = {
  isLoggedIn: false,
  uid: "",
  accountDeletionStatus: ACCOUNT_DELETION_STATUSES.NO_ATTEMPT,
  registerFailure: false,
  loginFailure: false,
  loginFailureMethod: null,
  passwordRecovery: false
};

/*
 * Responds to authentication actions and sets the appropriate state
 */
export function changeLoginState(
  state: AuthState | undefined,
  action: AuthAction
): AuthState {
  if (typeof state === "undefined") {
    return initialAuthState;
  }
  switch (action.type) {
    case USER_AUTHENTICATED:
    case REGISTER_SUCCESS:
      return Object.assign({}, state, {
        ...initialAuthState,
        isLoggedIn: true,
        uid: action.uid ?? ""
      });
    case REGISTER_FAILURE:
      return Object.assign({}, state, {
        ...initialAuthState,
        registerFailure: true
      });
    case USER_UNAUTHENTICATED:
    case LOGOUT:
      return Object.assign({}, state, {
        ...initialAuthState
      });
    case DELETE_ACCOUNT_SUCCESS:
      return Object.assign({}, state, {
        ...initialAuthState,
        accountDeletionStatus: ACCOUNT_DELETION_STATUSES.SUCCEEDED
      });
    case DELETE_ACCOUNT_FAILURE:
      return Object.assign({}, state, {
        accountDeletionStatus: ACCOUNT_DELETION_STATUSES.FAILED
      });
    case LOGIN_FAILURE:
      return Object.assign({}, state, {
        loginFailure: true,
        loginFailureMethod: action.method ?? null,
        passwordRecovery: false,
        registerFailure: false
      });
    case PASSWORD_RESET_EMAIL_SENT:
      return Object.assign({}, state, {
        passwordRecovery: true,
        loginFailure: false,
        loginFailureMethod: null,
        registerFailure: false
      });
    case REQUEST_DELETE_ACCOUNT:
    case LOGIN_SUCCESS:
    case REQUEST_REGISTER_EMAIL_PASSWORD:
    case REQUEST_LOGIN_EMAIL_PASSWORD:
    case REQUEST_LOGIN_GOOGLE:
    default:
      return state;
  }
}
